import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import enTranslations from '@/i18n/en.json';

export type Language = 'en';

interface I18nContextType {
  language: Language;
  t: (key: string) => string;
}

export const useI18n = (): I18nContextType => {
  const navigate = useNavigate();
  const location = useLocation();

  // Helper to get nested translation
  const getNestedValue = (obj: any, path: string): string => {
    const value = path.split('.').reduce((current, key) => current?.[key], obj);
    if (value && typeof value === 'string') return value;
    
    // Final fallback: convert key to readable text
    console.warn(`Missing translation: ${path}, using fallback`);
    const fallback = path.split('.').pop()?.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2') || 'Missing';
    return fallback.charAt(0).toUpperCase() + fallback.slice(1);
  };

  const t = (key: string): string => {
    return getNestedValue(enTranslations, key);
  };

  // Effect to handle redirects - remove language prefixes
  useEffect(() => {
    const pathname = location.pathname;
    
    // Redirect Spanish routes to English equivalents
    if (pathname.startsWith('/es/')) {
      const englishRoute = pathname.replace('/es/', '/');
      navigate(englishRoute === '/' ? '/' : englishRoute, { replace: true });
      return;
    }
    
    // Redirect /en/ routes to clean routes
    if (pathname.startsWith('/en/')) {
      const cleanRoute = pathname.replace('/en/', '/');
      navigate(cleanRoute === '/' ? '/' : cleanRoute, { replace: true });
      return;
    }
    
    // Handle exact /en and /es routes
    if (pathname === '/en' || pathname === '/es') {
      navigate('/', { replace: true });
      return;
    }
  }, [location.pathname, navigate]);

  // Update document language
  useEffect(() => {
    document.documentElement.lang = 'en';
  }, []);

  return { 
    language: 'en',
    t 
  };
};